import React from 'react'
import { Link } from 'react-router-dom'
import CardUI from '../../../widgets/layout/CardUI'
import DrawerUI from '../../../widgets/layout/DrawerUI'
import LoanStructureUpdateModalSF from './LoanStructureUpdateModalSF'
import LoanProperty2ListSF from './util/LoanProperty2ListSF.js'
import TermSheetPromoterUpdateModalSF from './TermSheetPromoterUpdateModalSF.js'
import { KvRowUI, KvSectionUI } from '../../../widgets/layout/KvRowUI'

const LoanStructureUI = ({ context, errorList, loan,
  updateMode,
  onRefreshClick, onUpdateClick, onUpdateCancel,
  directorList, propertyList,
  tokensDrawerMode, onTokensDrawerClick, onTokensDrawerCancel,
  selectedPromoter, promoterUpdateMode, onPromoterUpdateClick, onPromoterUpdateCancel,
}) => {
  const pledgeLabel = loan.pledgeOrNdu
    ? loan.pledgeOrNdu.charAt(0).toUpperCase() + loan.pledgeOrNdu.slice(1)
    : '—'
  const escrowLabel = !loan.escrow ? '—'
    : loan.escrow === 'b2b' ? 'B2B'
      : loan.escrow.charAt(0).toUpperCase() + loan.escrow.slice(1)

  return (
  <>
  <CardUI title={
    <div className='g_flex_h'>
      <span>Loan structure</span>
      <span className='icon icon-pencil g_link_2' onClick={onUpdateClick} />
    </div>
  }>
    <KvSectionUI label='Security & escrow' />
    <KvRowUI label='Pledge / NDU' value={pledgeLabel} />
    <KvRowUI label='Escrow' value={escrowLabel} />
    <KvRowUI label={
      <div>
        <div>Special terms</div>
        <span className='g_muted g_tiny'>Not used in term sheet</span>
      </div>
    } value={loan.specialTerms ? 'Yes' : 'No'} />

    <KvSectionUI label='Resolutions' />
    <KvRowUI label={
      <div>
        <div>Board meeting date</div>
        <span className='g_muted g_tiny'>Not used in term sheet</span>
      </div>
    } value={loan.resolutionSign.boardMeetingDateF1} />
    <KvRowUI label={
      <div>
        <div>Shareholder meeting date</div>
        <span className='g_muted g_tiny'>Not used in term sheet</span>
      </div>
    } value={loan.resolutionSign.shareholderMeetingDateF1} />

    <TokensSummaryUI
      loan={loan}
      directorList={directorList}
      propertyList={propertyList}
      onTokensDrawerClick={onTokensDrawerClick}
    />

    <LoanStructureUpdateModalSF
      context={context}
      loan={loan}
      updateMode={updateMode}
      onCancelClick={onUpdateCancel}
      onRefreshClick={onRefreshClick}
    />
  </CardUI>

  <TermSheetTokensDrawerUI
    context={context}
    loan={loan}
    directorList={directorList}
    propertyList={propertyList}
    isOpen={tokensDrawerMode}
    onClose={onTokensDrawerCancel}
    onRefreshClick={onRefreshClick}
    selectedPromoter={selectedPromoter}
    promoterUpdateMode={promoterUpdateMode}
    onPromoterUpdateClick={onPromoterUpdateClick}
    onPromoterUpdateCancel={onPromoterUpdateCancel}
  />
  </>
  )
}

const TokensSummaryUI = ({ loan, directorList, propertyList, onTokensDrawerClick }) => {
  const termSheetPropertyList = propertyList.filter((property) =>
    property.loanDocTypes.includes('term_sheet'))
  const generalCount = termSheetPropertyList.filter((property) => property.category === 'general_uw').length
  const coborrowerCount = termSheetPropertyList.filter((property) => property.category === 'ppa_uw').length
  const promoterCount = directorList.length
  const isPpa = loan.loanSubType === 'ppa'

  return (
    <>
      <KvSectionUI label='Term Sheet tokens' />
      <div className='g_flex_h'>
        <span className='g_small'>
          {generalCount} general
          {isPpa ? ` · ${coborrowerCount} co-borrower` : ''}
          {' · '}{promoterCount} promoter{promoterCount === 1 ? '' : 's'}
        </span>
        <span className='g_link' onClick={onTokensDrawerClick}>
          Open tokens →
        </span>
      </div>
    </>
  )
}

const TermSheetTokensDrawerUI = ({
  context, loan, directorList, propertyList, isOpen, onClose, onRefreshClick,
  selectedPromoter, promoterUpdateMode, onPromoterUpdateClick, onPromoterUpdateCancel,
}) => {
  const termSheetPropertyList = propertyList.filter((property) =>
    property.loanDocTypes.includes('term_sheet'))
  const generalCount = termSheetPropertyList.filter((property) => property.category === 'general_uw').length
  const coborrowerCount = termSheetPropertyList.filter((property) => property.category === 'ppa_uw').length

  return (
    <DrawerUI
      isOpen={isOpen}
      title='Term Sheet tokens'
      onClose={onClose}
      width='760px'
    >
      <p className='g_muted g_small g_m_bottom_100'>
        Tokens used in the term sheet document
        <Link className='g_m_left_50' to={`/su/loans/${loan.loanId}/tokenS`}>
          Open full Information →
        </Link>
      </p>

      <LoanProperty2ListSF context={context} loan={loan} propertyList={termSheetPropertyList}
        category='general_uw' categoryLabel={`General tokens (${generalCount})`}
        onRefreshClick={onRefreshClick} />
      <TermSheetPromoterListUI
        context={context}
        loan={loan}
        directorList={directorList}
        selectedPromoter={selectedPromoter}
        promoterUpdateMode={promoterUpdateMode}
        onPromoterUpdateClick={onPromoterUpdateClick}
        onPromoterUpdateCancel={onPromoterUpdateCancel}
        onRefreshClick={onRefreshClick}
      />
      {loan.loanSubType === 'ppa' &&
        <LoanProperty2ListSF context={context} loan={loan} propertyList={termSheetPropertyList}
          category='ppa_uw' categoryLabel={`Co-borrower tokens (${coborrowerCount})`}
          onRefreshClick={onRefreshClick} />
      }
    </DrawerUI>
  )
}

const TermSheetPromoterListUI = ({
  context, loan, directorList,
  selectedPromoter, promoterUpdateMode,
  onPromoterUpdateClick, onPromoterUpdateCancel, onRefreshClick,
}) =>
  <CardUI className='g_m_top_100' title={`Promoter tokens (${directorList.length})`}>
    {
      directorList.map((accountUser) => {
        const flags = [
          accountUser.guarantor ? 'Guarantor' : null,
          accountUser.pledgor ? 'Pledgor' : null,
          accountUser.sharePledgedPercent != null && accountUser.sharePledgedPercent !== ''
            ? `${accountUser.sharePledgedPercent}% pledged` : null,
        ].filter(Boolean)

        return (
          <div key={accountUser.userId} className='g_flex_h g_flex_top g_m_top_50'>
            <div>
              <div>{accountUser.name}</div>
              <span className='g_muted g_small'>
                {flags.length ? flags.join(' · ') : 'No guarantor or pledge'}
              </span>
            </div>
            <span className='icon icon-pencil g_link_2'
              data-user-id={accountUser.userId} onClick={onPromoterUpdateClick} />
          </div>
        )
      })
    }
    <TermSheetPromoterUpdateModalSF
      context={context}
      loan={loan}
      selectedUser={selectedPromoter}
      updateMode={promoterUpdateMode}
      onCancelClick={onPromoterUpdateCancel}
      onRefreshClick={onRefreshClick}
    />
  </CardUI>

export default LoanStructureUI
